import React, { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Loader2, Upload, ArrowRight } from "lucide-react";
import { t } from "@/content/jobs";

export default function JobApplicationForm({ job, lang, T, onBack }) {
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState(null);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", country: "", city: "",
    linkedin: "", portfolio: "", cover: "", cv: null, start: "",
    source: "", consent: false
  });
  const fileRef = useRef(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const words = f.cover.trim().split(/\s+/).filter(Boolean).length;
  const valid = f.first_name.trim() && f.last_name.trim()
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)
    && f.cover.trim().length > 20 && words <= 500
    && f.cv && f.cv.size <= 10 * 1024 * 1024 && f.consent;

  const L = {
    title:    { en: "Apply for this role",      fr: "Candidater pour ce poste" },
    first:    { en: "First name *",              fr: "Prénom *" },
    last:     { en: "Last name *",               fr: "Nom *" },
    email:    { en: "Email *",                   fr: "Email *" },
    country:  { en: "Country",                   fr: "Pays" },
    city:     { en: "City",                      fr: "Ville" },
    linkedin: { en: "LinkedIn URL",              fr: "URL LinkedIn" },
    portfolio:{ en: "Portfolio URL",             fr: "URL portfolio" },
    cover:    { en: "Why you, why now? * (500 words max)", fr: "Pourquoi vous, pourquoi maintenant ? * (500 mots max)" },
    words:    { en: "words",                     fr: "mots" },
    cv:       { en: "CV (.pdf, .doc, .docx — 10MB max) *", fr: "CV (.pdf, .doc, .docx — 10 Mo max) *" },
    choose:   { en: "Choose file",               fr: "Choisir un fichier" },
    none:     { en: "No file selected",          fr: "Aucun fichier sélectionné" },
    start:    { en: "Earliest start date",       fr: "Date de début au plus tôt" },
    source:   { en: "How did you hear about us?", fr: "Comment avez-vous entendu parler de nous ?" },
    consent:  { en: "I consent to Bootpreneurs processing my data for this application per the Privacy Policy. *",
                fr: "Je consens à ce que Bootpreneurs traite mes données pour cette candidature conformément à la Politique de confidentialité. *" },
    submit:   { en: "Submit application",        fr: "Envoyer ma candidature" },
    sending:  { en: "Submitting…",                fr: "Envoi en cours…" },
    ok_h:     { en: "Application received",       fr: "Candidature reçue" },
    ok_body:  { en: "Thanks, {name}. Every application is reviewed personally and you'll hear back within 7 days. If you don't, email careers@bootpreneurs.com directly.",
                fr: "Merci, {name}. Chaque candidature est examinée personnellement et vous aurez une réponse sous 7 jours. Sans réponse, écrivez directement à careers@bootpreneurs.com." },
    back:     { en: "Back to careers",            fr: "Retour aux carrières" },
    retry:    { en: "Try again",                  fr: "Réessayer" }
  };

  const submit = async () => {
    if (!valid) return;
    setStatus("uploading");
    setErrorMsg(null);
    try {
      const timestamp = Date.now();
      const rand = Math.random().toString(36).slice(2, 8);
      const safeName = f.cv.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${job.slug}/${timestamp}-${rand}-${safeName}`;
      const { error: uploadErr } = await supabase.storage
        .from("job-applications")
        .upload(path, f.cv, { cacheControl: "3600", upsert: false });
      if (uploadErr) throw new Error(uploadErr.message);

      // Store the storage path in cv_url; admins can sign URLs on demand.
      const cv_url = path;

      setStatus("submitting");

      const { data, error } = await supabase.functions.invoke("submit_job_application", {
        body: {
          job_posting_id: job.id,
          applicant_email: f.email,
          first_name: f.first_name,
          last_name: f.last_name,
          country: f.country || null,
          city: f.city || null,
          linkedin_url: f.linkedin || null,
          portfolio_url: f.portfolio || null,
          cover_note: f.cover,
          cv_url,
          cv_filename: f.cv.name,
          cv_size_bytes: f.cv.size,
          earliest_start_date: f.start || null,
          source: f.source || null,
          consent_data_processing: f.consent,
          lang
        }
      });
      if (error) throw new Error(error.message || "Submit failed");
      if (data?.error) throw new Error(data.error);
      setStatus("ok");
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || String(err));
      setStatus("error");
    }
  };

  if (status === "ok") {
    return (
      <div style={{
        borderRadius: 24, backgroundColor: T.bgElev, border: `1px solid ${T.border}`,
        padding: "56px 40px", textAlign: "center"
      }}>
        <div style={{
          margin: "0 auto 24px", width: 64, height: 64, borderRadius: "50%",
          backgroundColor: "rgba(16,185,129,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <CheckCircle2 size={32} color={T.success} strokeWidth={2} />
        </div>
        <h2 style={{ fontSize: "1.875rem", fontWeight: 700, color: T.text, marginBottom: 16, letterSpacing: "-0.02em" }}>
          {t(L.ok_h, lang)}
        </h2>
        <p style={{ maxWidth: 520, margin: "0 auto 32px", color: T.textMute, lineHeight: 1.65 }}>
          {t(L.ok_body, lang).replace("{name}", f.first_name)}
        </p>
        <button onClick={onBack} style={{
          padding: "10px 20px", borderRadius: 10, border: "none",
          color: T.primary, backgroundColor: "transparent", fontWeight: 600, cursor: "pointer", fontSize: 14
        }}>
          ← {t(L.back, lang)}
        </button>
      </div>
    );
  }

  const inp = {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    backgroundColor: T.bgElev, border: `1px solid ${T.border}`,
    color: T.text, fontSize: 14, outline: "none", boxSizing: "border-box"
  };
  const lbl = { display: "block", fontSize: 13, fontWeight: 500, color: T.textMute, marginBottom: 6 };
  const row2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };

  return (
    <div style={{ borderRadius: 24, backgroundColor: T.bgElev, border: `1px solid ${T.border}`, padding: "32px" }}>
      <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: T.text, marginBottom: 20, letterSpacing: "-0.02em" }}>
        {t(L.title, lang)}: {t(job.title, lang).replace(/\n/g, " ")}
      </h2>
      <div style={{ ...row2, marginBottom: 16 }}>
        <div><label style={lbl}>{t(L.first, lang)}</label>
          <input type="text" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} style={inp} /></div>
        <div><label style={lbl}>{t(L.last, lang)}</label>
          <input type="text" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} style={inp} /></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>{t(L.email, lang)}</label>
        <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} style={inp} /></div>
      <div style={{ ...row2, marginBottom: 16 }}>
        <div><label style={lbl}>{t(L.country, lang)}</label>
          <input type="text" value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="FR, SN, NG, BR…" style={inp} /></div>
        <div><label style={lbl}>{t(L.city, lang)}</label>
          <input type="text" value={f.city} onChange={(e) => set("city", e.target.value)} style={inp} /></div>
      </div>
      <div style={{ ...row2, marginBottom: 16 }}>
        <div><label style={lbl}>{t(L.linkedin, lang)}</label>
          <input type="url" value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} style={inp} /></div>
        <div><label style={lbl}>{t(L.portfolio, lang)}</label>
          <input type="url" value={f.portfolio} onChange={(e) => set("portfolio", e.target.value)} style={inp} /></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>{t(L.cover, lang)}</label>
        <textarea rows={6} value={f.cover} onChange={(e) => set("cover", e.target.value)}
          style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
        <div style={{ fontSize: 11, marginTop: 4, color: words > 500 ? T.danger : T.textFaint }}>
          {words} / 500 {t(L.words, lang)}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>{t(L.cv, lang)}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button type="button" onClick={() => fileRef.current?.click()}
            style={{ display: "inline-flex", alignItems: "center", padding: "8px 14px", borderRadius: 8,
              border: `1px solid ${T.border}`, backgroundColor: T.bgElev, color: T.textMute,
              fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            <Upload size={14} style={{ marginRight: 8 }} /> {t(L.choose, lang)}
          </button>
          <span style={{ fontSize: 13, color: T.textFaint }}>{f.cv ? f.cv.name : t(L.none, lang)}</span>
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }}
          onChange={(e) => set("cv", e.target.files?.[0] || null)} />
      </div>
      <div style={{ ...row2, marginBottom: 16 }}>
        <div><label style={lbl}>{t(L.start, lang)}</label>
          <input type="date" value={f.start} onChange={(e) => set("start", e.target.value)} style={inp} /></div>
        <div><label style={lbl}>{t(L.source, lang)}</label>
          <select value={f.source} onChange={(e) => set("source", e.target.value)} style={inp}>
            <option value=""></option>
            <option value="referral">{lang === "fr" ? "Recommandation" : "Referral"}</option>
            <option value="linkedin">LinkedIn</option>
            <option value="twitter">Twitter / X</option>
            <option value="direct">{lang === "fr" ? "Recherche directe" : "Direct search"}</option>
            <option value="other">{lang === "fr" ? "Autre" : "Other"}</option>
          </select>
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", marginBottom: 24 }}>
        <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)}
          style={{ marginTop: 3, accentColor: T.primary, width: 16, height: 16 }} />
        <span style={{ fontSize: 13, color: T.textMute, lineHeight: 1.55 }}>{t(L.consent, lang)}</span>
      </label>
      {status === "error" && (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, backgroundColor: T.dangerSoft, color: T.danger, fontSize: 13 }}>
          {errorMsg}
          <button onClick={() => setStatus("idle")} style={{ marginLeft: 12, textDecoration: "underline", background: "none", border: "none", color: T.danger, cursor: "pointer" }}>
            {t(L.retry, lang)}
          </button>
        </div>
      )}
      <button type="button" onClick={submit} disabled={!valid || status === "uploading" || status === "submitting"}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "14px 28px", borderRadius: 12, border: "none",
          backgroundColor: valid && status === "idle" ? T.primary : T.textFaint,
          color: "#FFFFFF", fontSize: 14, fontWeight: 600,
          cursor: valid && status === "idle" ? "pointer" : "not-allowed", gap: 8
        }}>
        {(status === "uploading" || status === "submitting") && <Loader2 size={16} style={{ animation: "bp_spin 1s linear infinite" }} />}
        {status === "uploading" || status === "submitting" ? t(L.sending, lang) : (<>{t(L.submit, lang)} <ArrowRight size={16} strokeWidth={2.5} /></>)}
      </button>
    </div>
  );
}
